import React, { useEffect, useMemo } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch
} from 'react-router';
import { LearnerTrainingStatus } from '~/data';
import { Navigation } from '~/training/navigation/Navigation';
import { Settings } from '~/training/settings/Settings';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import { MediaPlayerProvider } from '~/ui/video-player/MediaPlayerProvider';
import { NotFound } from '../ui/not-found/NotFound';
import { DefaultSequence } from './DefaultSequence';
import { Feedback } from './feedback/Feedback';
import { Help } from './help/Help';
import { Onboarding } from './onboarding/Onboarding';
import { Form } from './onboarding/Form';
import { ProgressBar } from './progress-bar/ProgressBar';
import { Sequence } from './sequence/Sequence';
import { Chat } from './chat/Chat';
import { Chapter, TrainingState, useTrainingStore } from './state';
import { InitTrainingState } from './state/InitTrainingState';
import { Success } from './success/Success';
import { PassFail } from './passfail/Passfail';
import { DemoMode } from '~/ui/video-player/DemoMode';
import { GlobalMediaProvider } from '~/ui/video-player/GlobalMediaProvider';
import { SequenceProvider } from '~/training/sequence/SequenceStore';
import { Thumbs } from './thumbs-up-down/Thumbs';
import { select } from '~/lib/select';
import { isIOS, isIOS13, isIPad13, isIPhone13 } from 'react-device-detect';
import { useQueryParams } from '~/lib/useQuery';
import { TimeRemainingPill } from './courseTimers/TimeRemainingPill';
import { TimerStillRunningModal } from './courseTimers/TimerStillRunningModal';
import { useCourseReviewTimer } from './courseTimers/useCourseReviewTimer';
import { DocumentTitle } from '~/lib/a11y/DocumentTitle';

const selector = (s: TrainingState) =>
  select(
    s,
    'status',
    'player',
    'items',
    'activeItemPosition',
    'promptProfile',
    'featureShowChatbot'
  );

const getIndex = (chapter: Chapter, position: number) =>
  Math.max(0, Math.min(chapter.sequenceCount, position - chapter.position));

export const Training = () => {
  useCourseReviewTimer();
  const {
    status,
    player,
    items,
    activeItemPosition,
    promptProfile,
    featureShowChatbot
  } = useTrainingStore(selector);
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const params = useQueryParams();

  const { segments, lastSequence } = useMemo(() => {
    const c = items
      .filter(i => i.kind === 'chapter')
      .map((c: Chapter) => ({
        id: c.id,
        length: c.sequenceCount,
        index: getIndex(c, activeItemPosition)
      }));

    const s = items.filter(i => i.kind === 'sequence') || [];

    return { segments: c, lastSequence: s[s.length - 1] };
  }, [items, activeItemPosition]);

  if (
    (player === 'demo' || player === 'demo_limited') &&
    segments &&
    segments.length
  ) {
    if (segments[3]) {
      const lastElement = segments[3];
      if (lastElement.index == 1) {
        if (isIOS || isIOS13 || isIPad13 || isIPhone13) {
          window.location.href = `${url}/${lastSequence?.id}`;
        } else {
          history.push(`${url}/${lastSequence?.id}`);
        }
      }
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const urlChunk = location.pathname.split('/');

  if (
    (urlChunk.length === 5 || (urlChunk.length === 6 && urlChunk[5] == '')) &&
    !/onboarding$/.test(location.pathname) &&
    status === LearnerTrainingStatus.Activated
  ) {
    if (promptProfile) {
      return <Redirect to={`${url}/form`.replace('//', '/')} />;
    } else {
      return <Redirect to={`${url}/onboarding`.replace('//', '/')} />;
    }
  }

  return (
    <GlobalMediaProvider>
      <DocumentTitle />
      <InitTrainingState>
        <Switch>
          <Route path={`${path}/onboarding`} exact>
            <Onboarding />
          </Route>
          <Route path={`${path}/form`} exact>
            <Form />
          </Route>
          <Route path={`${path}/passfail`} exact>
            <PassFail />
          </Route>
          <Route path={`${path}/feedback`} exact>
            <Feedback />
          </Route>
          <Route path={`${path}/success`} exact>
            <Success />
            <MediaPlayerProvider>
              <SequenceProvider>
                <Navigation />
              </SequenceProvider>
            </MediaPlayerProvider>
          </Route>
          <Route path={`${path}/:sequenceId`} exact>
            <MediaPlayerProvider>
              <SequenceProvider>
                <AudioPlayer />
                <Navigation />
                <TimeRemainingPill />
                {params.get('removeAllButtons') !== 'true' && <Settings />}
                {params.get('removeAllButtons') !== 'true' && <Help />}
                {params.get('removeAllButtons') !== 'true' && <Thumbs />}
                <ProgressBar />
                <Sequence />
                <DemoMode />
                {featureShowChatbot && <Chat />}
                <TimerStillRunningModal />
              </SequenceProvider>
            </MediaPlayerProvider>
          </Route>
          <Route path={`${path}`} exact>
            <DefaultSequence />
          </Route>
          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </InitTrainingState>
    </GlobalMediaProvider>
  );
};
